import type { AddressBlockProps } from "./address/types";
import Banner from "./banner";
import { Block, BlockType } from "./blocks-types";
import Button from "./button";
import DynamicText from "./dynamic-text";
import type { DynamicTextBlockProps } from "./dynamic-text/types";
import type { ButtonBlockProps } from "./button/types";
import CrossSell from "./cross-sell";
import type { CrossSellBlockProps } from "./cross-sell/types";
import DoubleButtons from "./double-buttons";
import type { DoubleButtonsBlockProps } from "./double-buttons/types";
import Logo from "./logo";
import Address from "./address";
import EstimatedDeliveryDate from "./estimated-delivery-date";
import type { LogoBlockProps } from "./logo/types";
import PackageContent from "./package-content";
import type { PackageContentBlockProps } from "./package-content/types";
import Paragraph from "./paragraph";
import type { ParagraphBlockProps } from "./paragraph/types";
import PickupPoint from "./pickup-point";
import type { PickupPointBlockProps } from "./pickup-point/types";
import Row from "./row";
import type { EstimatedDeliveryDateBlockProps } from "./estimated-delivery-date/types";
import { registerBlock } from "../lib/register-block";
import { BannerBlockProps } from "./banner/types";
import { RowBlockProps } from "./row/types";
import RateOrder from "./rate-order";
import type { RateOrderBlockProps } from "./rate-order/types";
import Separator from "./separator";
import type { SeparatorBlockProps } from "./separator/types";
import SocialNetworks from "./social-networks";
import type { SocialNetworksBlockProps } from "./social-networks/types";
import Wallet from "./wallet";

export * from "./button";
export * from "./cross-sell";
export * from "./double-buttons";
export * from "./dynamic-text";
export * from "./logo";
export * from "./package-content";
export * from "./paragraph";
export * from "./rate-order";
export * from "./separator";
export * from "./estimated-delivery-date";
export * from "./social-networks";
export * from "./stepper";
export * from "./wallet";
export * from "./return-line-items";
export * from "./return-store-address";
export * from "./return-shipping-instructions";
export * from "./return-download-label";
export * from "./return-request-date";
export * from "./return-shipping-instructions-title";
export * from "./return-discount-code";

export { BlockCategory } from "../lib/register-block";
export * from "./blocks-types";

import Stepper from "./stepper";

import LastStatus from "./last-status";
import { LastStatusBlockProps } from "./last-status/types";
import type { StepperBlockProps } from "./stepper/types";
import TrackingHistory from "./tracking-history";
import { TrackingHistoryBlockProps } from "./tracking-history/types";
import { WalletBlockProps } from "./wallet/types";
import ReturnLineItems from "./return-line-items";
import { ReturnLineItemsBlockProps } from "./return-line-items/types";
import ReturnStoreAddress from "./return-store-address";
import { ReturnStoreAddressBlockProps } from "./return-store-address/types";
import ReturnShippingInstructions from "./return-shipping-instructions";
import { ReturnShippingInstructionsBlockProps } from "./return-shipping-instructions/types";
import ReturnDownloadLabel from "./return-download-label";
import { ReturnDownloadLabelBlockProps } from "./return-download-label/types";
import ReturnRequestDate from "./return-request-date";
import { ReturnRequestDateBlockProps } from "./return-request-date/types";
import ReturnShippingInstructionsTitle from "./return-shipping-instructions-title";
import { ReturnShippingInstructionsTitleBlockProps } from "./return-shipping-instructions-title/types";
import ReturnDiscountCode from "./return-discount-code";
import { ReturnDiscountCodeBlockProps } from "./return-discount-code/types";

// Map of block types to their corresponding props types
const blockPropsMap = {
  wallet: {} as WalletBlockProps,
  paragraph: {} as ParagraphBlockProps,
  logo: {} as LogoBlockProps,
  "social-networks": {} as SocialNetworksBlockProps,
  "package-content": {} as PackageContentBlockProps,
  button: {} as ButtonBlockProps,
  "cross-sell": {} as CrossSellBlockProps,
  "double-buttons": {} as DoubleButtonsBlockProps,
  separator: {} as SeparatorBlockProps,
  "delivery-address": {} as AddressBlockProps,
  row: {} as RowBlockProps,
  "pickup-point": {} as PickupPointBlockProps,
  "estimated-delivery-date": {} as EstimatedDeliveryDateBlockProps,
  // "stepper": null,
  banner: {} as BannerBlockProps,
  stepper: {} as StepperBlockProps,
  "tracking-history": {} as TrackingHistoryBlockProps,
  "last-status": {} as LastStatusBlockProps,
  "dynamic-text": {} as DynamicTextBlockProps,
  "rate-order": {} as RateOrderBlockProps,
  "return-line-items": {} as ReturnLineItemsBlockProps,
  "return-store-address": {} as ReturnStoreAddressBlockProps,
  "return-shipping-instructions": {} as ReturnShippingInstructionsBlockProps,
  "return-download-label": {} as ReturnDownloadLabelBlockProps,
  "return-request-date": {} as ReturnRequestDateBlockProps,
  "return-shipping-instructions-title": {} as ReturnShippingInstructionsTitleBlockProps,
  "return-discount-code": {} as ReturnDiscountCodeBlockProps,
  // "carrier": null,
} as const satisfies Record<BlockType, unknown>;

type BlockPropsMap = typeof blockPropsMap;

type RegisteredBlock<T extends keyof BlockPropsMap> = ReturnType<
  typeof registerBlock<BlockPropsMap[T]>
>;

// Map of block types to their corresponding registered block components or null
// This is also used to define the editor's available blocks
export const editorBlocksLibrary: {
  [K in keyof BlockPropsMap]: RegisteredBlock<K> | null;
} = {
  wallet: Wallet,
  paragraph: Paragraph,
  logo: Logo,
  "social-networks": SocialNetworks,
  "package-content": PackageContent,
  button: Button,
  "cross-sell": CrossSell,
  "double-buttons": DoubleButtons,
  separator: Separator,
  "delivery-address": Address,
  banner: Banner,
  row: Row,
  "pickup-point": PickupPoint,
  "estimated-delivery-date": EstimatedDeliveryDate,
  // "stepper": null,
  stepper: Stepper,
  "tracking-history": TrackingHistory,
  "last-status": LastStatus,
  "dynamic-text": DynamicText,
  "rate-order": RateOrder,
  "return-line-items": ReturnLineItems,
  "return-store-address": ReturnStoreAddress,
  "return-shipping-instructions": ReturnShippingInstructions,
  "return-download-label": ReturnDownloadLabel,
  "return-request-date": ReturnRequestDate,
  "return-shipping-instructions-title": ReturnShippingInstructionsTitle,
  "return-discount-code": ReturnDiscountCode,
  // "carrier": null,
} satisfies Record<BlockType, unknown>;

export const getEditorBlockConfig = <T extends BlockType>(type: T) => {
  return editorBlocksLibrary[type] as RegisteredBlock<T> | null;
};

export function isBlockTranslated(locale: string, block: Block) {
  if (!block?.content) return true;

  const blockConfig = getEditorBlockConfig(block.type);

  if (blockConfig?.config?.isTranslated) {
    return blockConfig.config.isTranslated(locale, block);
  }

  // @ts-expect-error type not predictable here
  if (block.content[locale]?.html?.length) {
    return (
      //@ts-expect-error type not predictable here
      block.content[locale]?.html?.replace(/<[^>]+>/g, "").trim().length > 0
    );
  }
  // @ts-expect-error type not predictable here
  if (block.content[locale]?.text?.length) {
    return true;
  }

  // @ts-expect-error type not predictable here
  if (!!block.content[locale]?.length) return true;

  return false;
}
